/**
 * Global Token Modal Context
 * Manages the centralized insufficient tokens modal across the entire app
 */

import { createContext, type ReactNode, useCallback, useContext, useState } from "react";

import { InsufficientTokensModal } from "../components/ui/insufficient-tokens-modal";

type TokenModalData = {
  requiredTokens: number;
  currentTokens: number;
  featureName?: string;
  showUpgradeOption?: boolean;
  onNavigation?: () => void; // Callback to close parent dialogs when navigating
  onRetry?: () => void; // Callback to retry the failed operation
  isNetworkError?: boolean; // Whether this is a network error vs insufficient tokens
};

type TokenModalContextType = {
  showTokenModal: (data: TokenModalData) => void;
  hideTokenModal: () => void;
  isModalOpen: boolean;
};

const TokenModalContext = createContext<TokenModalContextType | null>(null);

export const useTokenModal = () => {
  const context = useContext(TokenModalContext);
  if (!context) {
    throw new Error("useTokenModal must be used within a TokenModalProvider");
  }
  return context;
};

type TokenModalProviderProps = {
  children: ReactNode;
};

export const TokenModalProvider = ({ children }: TokenModalProviderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<TokenModalData | null>(null);

  const showTokenModal = useCallback((data: TokenModalData) => {
    setModalData(data);
    setIsModalOpen(true);
  }, []);

  const hideTokenModal = useCallback(() => {
    setIsModalOpen(false);
    setModalData(null);
  }, []);

  const contextValue = {
    showTokenModal,
    hideTokenModal,
    isModalOpen,
  };

  return (
    <TokenModalContext.Provider value={contextValue}>
      {children}

      {/* Global Insufficient Tokens Modal */}
      <InsufficientTokensModal
        isOpen={isModalOpen}
        requiredTokens={modalData?.requiredTokens}
        currentTokens={modalData?.currentTokens}
        featureName={modalData?.featureName}
        showUpgradeOption={modalData?.showUpgradeOption}
        onNavigation={modalData?.onNavigation}
        onRetry={modalData?.onRetry}
        isNetworkError={modalData?.isNetworkError}
        onClose={hideTokenModal}
      />
    </TokenModalContext.Provider>
  );
};
